<script setup lang="ts">
/* eslint-disable vue/no-undef-components */
import { computed, nextTick, ref, watch } from 'vue';
import LearnEditorAIPresetsTable from '@/components/learn/LearnEditorAIPresetsTable.vue';
import LearnEditorImageToolbarTable from '@/components/learn/LearnEditorImageToolbarTable.vue';
import LearnEditorTextAdvancedIntro from '@/components/learn/LearnEditorTextAdvancedIntro.vue';
import LearnEditorTextToolbarTable from '@/components/learn/LearnEditorTextToolbarTable.vue';
import LearnMarkdownView from '@/components/learn/LearnMarkdownView.vue';
import { i18n } from '@/i18n';
import {
  learnChapterBySlug,
  learnChapterDescription,
  learnChapterHeading,
  slugifyHeading,
} from '@/lib/learn-docs';
import { isNoindexTestHost } from '@/lib/site-host';
import {
  absoluteLocalizedUrl,
  buildPathHreflangLinks,
  ogLocaleFor,
  siteLocaleFromRouteParam,
} from '@/lib/site-locale';

const props = defineProps<{
  slug: string;
}>();

const route = useRoute();
const requestUrl = useRequestURL();
const siteLocale = computed(() => siteLocaleFromRouteParam(route.params.locale));
const source = ref('');
const loadError = ref('');
const pageRef = ref<HTMLElement | null>(null);

function ensureHeadingIds() {
  const root = pageRef.value;
  if (!root) return;
  root.querySelectorAll('h2, h3').forEach((el) => {
    if (el.id) return;
    const text = el.textContent?.trim() ?? '';
    if (!text) return;
    el.id = slugifyHeading(text);
  });
}
const EDITOR_TEXT_TOOLBAR_MARKER = '<!-- DOC_TEXT_TOOLBAR_TABLE -->';
const EDITOR_TEXT_ADVANCED_INTRO_MARKER = '> <!-- DOC_TEXT_ADVANCED_INTRO -->';
const EDITOR_AI_PRESETS_TABLE_MARKER = '<!-- DOC_AI_PRESETS_TABLE -->';
const EDITOR_IMAGE_TOOLBAR_MARKER = '<!-- DOC_IMAGE_TOOLBAR_TABLE -->';
const INTRO_SPLIT_MARKER = '<!-- DOC_INTRO_CAROUSEL -->';
const INTRO_EVERY_STEP_MARKER = '<!-- DOC_INTRO_EVERY_STEP -->';

const current = computed(() => learnChapterBySlug(props.slug));
const pageHeading = computed(() =>
  current.value ? learnChapterHeading(props.slug) : 'Veeso Learn'
);
const pageTitle = computed(() =>
  current.value ? `${pageHeading.value} | Veeso Learn` : 'Veeso Learn'
);
const description = computed(() => {
  const localized = learnChapterDescription(props.slug);
  if (localized) return localized;
  return (
    current.value?.description ||
    'Veeso product docs for prompts, editing, exports, credits, troubleshooting, and practical workflows.'
  );
});
const learnRoutePath = computed(() =>
  props.slug === 'welcome' ? '/learn' : `/learn/${props.slug}`
);
const canonicalUrl = computed(() => absoluteLocalizedUrl(siteLocale.value, learnRoutePath.value));
const ogImageUrl = computed(() => 'https://veeso.ai/Veeso_og.jpg');
const robotsContent = computed(() => {
  const host = requestUrl.hostname;
  return isNoindexTestHost(host)
    ? 'noindex, nofollow'
    : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1';
});

const editorTextToolbarSplit = computed(() => {
  if (props.slug !== 'editor' || !source.value) return null;
  const index = source.value.indexOf(EDITOR_TEXT_TOOLBAR_MARKER);
  if (index === -1) return null;
  return {
    before: source.value.slice(0, index).trim(),
    after: source.value.slice(index + EDITOR_TEXT_TOOLBAR_MARKER.length).trim(),
  };
});

const editorTextAdvancedIntroSplit = computed(() => {
  const tail = editorTextToolbarSplit.value?.after;
  if (!tail) return null;
  const index = tail.indexOf(EDITOR_TEXT_ADVANCED_INTRO_MARKER);
  if (index === -1) return null;
  return {
    before: tail.slice(0, index).trim(),
    after: tail.slice(index + EDITOR_TEXT_ADVANCED_INTRO_MARKER.length).trim(),
  };
});

const editorAIPresetsTableSplit = computed(() => {
  const tail = editorTextAdvancedIntroSplit.value?.after;
  if (!tail) return null;
  const index = tail.indexOf(EDITOR_AI_PRESETS_TABLE_MARKER);
  if (index === -1) return null;
  return {
    before: tail.slice(0, index).trim(),
    after: tail.slice(index + EDITOR_AI_PRESETS_TABLE_MARKER.length).trim(),
  };
});

const editorImageToolbarSplit = computed(() => {
  const tail = editorAIPresetsTableSplit.value?.after;
  if (!tail) return null;
  const index = tail.indexOf(EDITOR_IMAGE_TOOLBAR_MARKER);
  if (index === -1) return null;
  return {
    before: tail.slice(0, index).trim(),
    after: tail.slice(index + EDITOR_IMAGE_TOOLBAR_MARKER.length).trim(),
  };
});

const introSplit = computed(() => {
  if (props.slug !== 'welcome' || !source.value) return null;
  const index = source.value.indexOf(INTRO_SPLIT_MARKER);
  if (index === -1) return null;
  return {
    before: source.value.slice(0, index).trim(),
    after: source.value.slice(index + INTRO_SPLIT_MARKER.length).trim(),
  };
});

const introAfterEveryStep = computed(() => {
  if (!introSplit.value) return null;
  const after = introSplit.value.after;
  const index = after.indexOf(INTRO_EVERY_STEP_MARKER);
  if (index === -1) return null;
  return {
    before: after.slice(0, index).trim(),
    after: after.slice(index + INTRO_EVERY_STEP_MARKER.length).trim(),
  };
});

watch(
  () => [props.slug, i18n.locale] as const,
  async ([slug]) => {
    loadError.value = '';
    const chapter = learnChapterBySlug(slug);

    if (!chapter) {
      throw createError({
        statusCode: 404,
        statusMessage: 'Page not found',
      });
    }

    try {
      const mod = await chapter.load();
      source.value = mod.default;
    } catch (error) {
      loadError.value = error instanceof Error ? error.message : 'Failed to load document';
      source.value = '';
    }
  },
  { immediate: true }
);

watch(source, async () => {
  await nextTick();
  await nextTick();
  ensureHeadingIds();
});

watch(
  () => route.fullPath,
  async () => {
    await nextTick();
    if (process.client) {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }
);

useHead(() => ({
  title: pageTitle.value,
  htmlAttrs: { lang: siteLocale.value },
  link: [
    { rel: 'canonical', href: canonicalUrl.value },
    ...buildPathHreflangLinks(learnRoutePath.value),
  ],
  meta: [
    { key: 'description', name: 'description', content: description.value },
    { key: 'robots', name: 'robots', content: robotsContent.value },
    { key: 'og:title', property: 'og:title', content: pageTitle.value },
    { key: 'og:description', property: 'og:description', content: description.value },
    { key: 'og:type', property: 'og:type', content: 'article' },
    { key: 'og:url', property: 'og:url', content: canonicalUrl.value },
    { key: 'og:locale', property: 'og:locale', content: ogLocaleFor(siteLocale.value) },
    { key: 'og:image', property: 'og:image', content: ogImageUrl.value },
    { key: 'og:image:width', property: 'og:image:width', content: '1200' },
    { key: 'og:image:height', property: 'og:image:height', content: '630' },
    { key: 'twitter:card', name: 'twitter:card', content: 'summary_large_image' },
    { key: 'twitter:site', name: 'twitter:site', content: '@veesoai' },
    { key: 'twitter:title', name: 'twitter:title', content: pageTitle.value },
    { key: 'twitter:description', name: 'twitter:description', content: description.value },
    { key: 'twitter:image', name: 'twitter:image', content: ogImageUrl.value },
  ],
}));
</script>

<template>
  <div ref="pageRef" :class="{ 'doc-editor-md': slug === 'editor' }">
    <header v-if="current" class="mb-6 sm:mb-8">
      <h1
        class="text-xl sm:text-2xl md:text-[28px] font-bold tracking-tight text-black/90 dark:text-white/90"
      >
        {{ pageHeading }}
      </h1>
    </header>

    <p v-if="loadError" class="text-sm text-red-600 dark:text-red-400">
      {{ loadError }}
    </p>

    <template v-else-if="introSplit">
      <LearnMarkdownView :source="introSplit.before" />
      <section
        class="mt-6 sm:mt-8 min-w-0 overflow-hidden rounded-xl sm:rounded-2xl"
        aria-label="Use-case carousel"
      >
        <FeaturesEveryScene embedded />
      </section>
      <template v-if="introAfterEveryStep">
        <LearnMarkdownView
          v-if="introAfterEveryStep.before"
          :source="introAfterEveryStep.before"
          continuation
        />
        <section
          class="mt-6 sm:mt-8 min-w-0 overflow-visible"
          aria-label="Generate, edit, and export demo"
        >
          <FeaturesEveryStep embedded />
        </section>
        <LearnMarkdownView
          v-if="introAfterEveryStep.after"
          :source="introAfterEveryStep.after"
          continuation
        />
      </template>
      <LearnMarkdownView v-else :source="introSplit.after" continuation />
    </template>
    <template v-else-if="editorTextToolbarSplit">
      <LearnMarkdownView :source="editorTextToolbarSplit.before" />
      <LearnEditorTextToolbarTable />
      <template v-if="editorTextAdvancedIntroSplit">
        <LearnMarkdownView
          v-if="editorTextAdvancedIntroSplit.before"
          :source="editorTextAdvancedIntroSplit.before"
          continuation
        />
        <LearnEditorTextAdvancedIntro />
        <template v-if="editorAIPresetsTableSplit">
          <LearnMarkdownView
            v-if="editorAIPresetsTableSplit.before"
            :source="editorAIPresetsTableSplit.before"
            continuation
          />
          <LearnEditorAIPresetsTable />
          <template v-if="editorImageToolbarSplit">
            <LearnMarkdownView
              v-if="editorImageToolbarSplit.before"
              :source="editorImageToolbarSplit.before"
              continuation
            />
            <LearnEditorImageToolbarTable />
            <LearnMarkdownView
              v-if="editorImageToolbarSplit.after"
              :source="editorImageToolbarSplit.after"
              continuation
            />
          </template>
          <LearnMarkdownView
            v-else-if="editorAIPresetsTableSplit.after"
            :source="editorAIPresetsTableSplit.after"
            continuation
          />
        </template>
        <LearnMarkdownView
          v-else-if="editorTextAdvancedIntroSplit.after"
          :source="editorTextAdvancedIntroSplit.after"
          continuation
        />
      </template>
      <LearnMarkdownView
        v-else-if="editorTextToolbarSplit.after"
        :source="editorTextToolbarSplit.after"
        continuation
      />
    </template>
    <LearnMarkdownView v-else :source="source" />
  </div>
</template>
